import type { ReactNode } from 'react';

import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import type { DialogSurfaceProps } from './Dialog.types';
import {
  DIALOG_PANEL_RADIUS_PX,
  DIALOG_TITLE_CONTROLS_CLASS,
  dialogPanelVariants,
} from './Dialog.variants';
import { DialogContext } from './DialogContext';
import { DialogGlassBackground } from './DialogGlassBackground';
import {
  DialogBack,
  DialogBody,
  DialogClose,
  DialogFooter,
  DialogHeader,
  DialogHeaderIcon,
  DialogMedia,
  DialogSubheading,
  DialogTitle,
  DialogTopRight,
} from './DialogParts';

/**
 * The Dialog panel — cross-platform, platform-agnostic. The platform files
 * (`Dialog.tsx` / `Dialog.native.tsx`) own the overlay, portal/host, dismissal
 * and entry animation, and mount this surface inside.
 *
 * Two modes, one render path (both go through the compound parts in
 * `DialogParts.tsx`, so they can't drift):
 *
 * - **Prop-driven** (a `title` is given): renders the standard Figma
 *   skeleton — header slot / media / back row, title + subheading, body,
 *   footer. The close button is ALWAYS top-right: inline in the top row
 *   normally, pinned over the panel corner when `headerMedia` pushes the
 *   title down.
 * - **Composed** (no `title`): children render directly inside the panel;
 *   the caller assembles `DialogHeader` / `DialogTitle` / `DialogFooter` /
 *   `DialogClose` etc. themselves (context supplies align + onClose).
 *
 * Vertical rhythm — ONE scale for every variant, measured off the frame
 * geometry of Figma node 538:14327 (Content frame at 24,24; Header bottom 56 →
 * Title Group 72; Title bottom 32 → Description 36; Title Group bottom 132 →
 * Slot 148; Content bottom 512 → Footer 532):
 *
 *   panel padding             24px  → `p-6`    (dialogPanelVariants)
 *   header slot → title       16px  → `gap-4`
 *   title → description        4px  → `gap-1`  (DialogHeader)
 *   description → content     16px  → `gap-4`
 *   content → footer          20px  → `gap-5`  (panel gap, dialogPanelVariants)
 *
 * ALIGNMENT — every branch pins its close X (plus the back arrow / header-right
 * slot) through one `titleControls` cluster carrying
 * `DIALOG_TITLE_CONTROLS_CLASS`, and composed dialogs get the same rule from
 * `DialogTopRight`'s default `align="title"`. There is exactly one place each
 * for the in-flow and the pinned case, so a close button cannot end up 4px off
 * its title in some layouts and right in others.
 *
 * The two 16px gaps are the same step: whatever sits above the title block is
 * 16px off it, and the title block is 16px off the body. Earlier revisions had
 * the second one at 8px in the slot/media branches only, which is what made
 * the header variants disagree with each other.
 */
export function DialogSurface({
  title,
  subheading,
  headerClassName,
  size = 'sm',
  hug = false,
  align = 'left',
  headerIcon,
  headerSlot,
  headerMedia,
  headerRight,
  onBack,
  showClose = true,
  closeAccessibilityLabel = 'Close',
  backAccessibilityLabel = 'Back',
  footer,
  footerLayout,
  children,
  bodyContained = false,
  bodySurface,
  bodyScroll = false,
  className,
  panelClassName,
  panelStyle,
  onClosePress,
  testID,
}: DialogSurfaceProps) {
  const centered = align === 'center';
  const { isDark } = useTheme();

  const closeButton = showClose ? (
    <DialogClose accessibilityLabel={closeAccessibilityLabel} />
  ) : null;

  // Header controls sit INLINE before the X, never on top of it — flexbox does
  // the "shift when a close button is there" for free; the margin is the only
  // thing that has to know, so it drops when there is no X to clear.
  //
  // The rows below are `items-start` (so a wrapping title keeps its first line
  // level with the X). That would hang shorter controls off the row's top edge
  // — the leaderboard's period Button is `xs` (32px) and its metric
  // SegmentedControl `xxs` (24px) against the X's 36px. Giving the slot the
  // close button's own height and centering inside it lines every control up
  // on the X's axis, without depending on the row height (which grows when the
  // title wraps).
  const headerRightSlot =
    headerRight != null ? (
      <View className={cn('shrink-0 flex-row items-center gap-1', closeButton && 'mr-2')}>
        {headerRight}
      </View>
    ) : null;

  // Every title row pins its controls through ONE cluster, so the close X (and
  // whatever sits beside it) lines up on the title the same way in all of them
  // — see DIALOG_TITLE_CONTROLS_CLASS.
  const titleControls =
    headerRightSlot != null || closeButton != null ? (
      <View className={DIALOG_TITLE_CONTROLS_CLASS}>
        {headerRightSlot}
        {closeButton}
      </View>
    ) : null;

  const titleBlock =
    title != null ? (
      <DialogHeader className={headerClassName}>
        <DialogTitle>{title}</DialogTitle>
        {subheading != null && <DialogSubheading>{subheading}</DialogSubheading>}
      </DialogHeader>
    ) : null;

  // The PANEL carries the height floor (per-size min-height in
  // Dialog.variants.ts, dropped by `hug`); the body just absorbs the slack.
  // DialogBody fills by default (`flex-1`), so whenever the min-height leaves
  // spare room it lands inside the body instead of as a gap above the footer.
  // A dialog with NO children has no body at all — there the footer's
  // `mt-auto` keeps the buttons pinned to the bottom edge.
  // `bodyScroll`: the wrappers between panel and body must all be allowed to
  // shrink (the web View reset pins flex-shrink to 0) so the body — not the
  // panel — absorbs the height cap and scrolls, keeping title + footer pinned.
  const interiorClass = cn('min-h-0 w-full grow gap-4', bodyScroll && 'shrink');

  const body =
    children != null ? (
      <DialogBody
        contained={bodyContained}
        surface={bodySurface}
        className={bodyScroll ? 'shrink web:overflow-y-auto' : undefined}>
        {children}
      </DialogBody>
    ) : null;

  const leadingSlot =
    headerIcon != null ? <DialogHeaderIcon icon={headerIcon} /> : (headerSlot ?? null);

  let interior: ReactNode;
  if (title == null) {
    // Composed mode — the caller owns the interior.
    interior = children;
  } else if (onBack != null) {
    // Back-button row: back · inline title block · close (node 4923:37929 —
    // the title sits flush after the 36px back button, no gap).
    interior = (
      // Same wrapper as every other branch: the body sits 16px under the
      // navigation row (Figma 4923:37929 — Navigation 0..36, Slot at y=52).
      // As a bare fragment these were panel children and inherited its 20px
      // gap instead.
      <View className={interiorClass}>
        <View className="w-full flex-row items-start">
          {/* -ml-2 (with mr-2 putting the title back at Figma's x=36): the
              36px ghost button centres a 20px glyph, so at the plain padding
              inset the arrow reads 8px indented from the body container
              beneath it. The margin aligns the GLYPH with the container edge. */}
          <View className={cn(DIALOG_TITLE_CONTROLS_CLASS, '-ml-2 mr-2')}>
            <DialogBack onPress={onBack} accessibilityLabel={backAccessibilityLabel} />
          </View>
          <View
            className={cn(
              'min-w-0 flex-1 gap-1',
              centered && 'items-center',
              headerClassName,
            )}>
            <DialogTitle>{title}</DialogTitle>
            {subheading != null && <DialogSubheading>{subheading}</DialogSubheading>}
          </View>
          {titleControls}
        </View>
        {body}
      </View>
    );
  } else if (headerMedia != null) {
    // Media header: image block first, title below — the close button stays
    // in the top-right CORNER of the panel, floating above the media.
    interior = (
      <>
        {showClose && (
          <DialogTopRight align="media">
            <DialogClose accessibilityLabel={closeAccessibilityLabel} />
          </DialogTopRight>
        )}
        <View className={interiorClass}>
          <DialogMedia>{headerMedia}</DialogMedia>
          <View className={interiorClass}>
            {titleBlock}
            {body}
          </View>
        </View>
      </>
    );
  } else if (leadingSlot != null) {
    // Slot row (icon chip / custom slot) + close, title block below.
    interior = (
      <View className={interiorClass}>
        {centered ? (
          <View className="w-full flex-row items-center">
            <View className="flex-1 flex-row justify-center">{leadingSlot}</View>
            {titleControls}
          </View>
        ) : (
          <View className="w-full flex-row items-center justify-between">
            {leadingSlot}
            {titleControls}
          </View>
        )}
        <View className={interiorClass}>
          {titleBlock}
          {body}
        </View>
      </View>
    );
  } else {
    // Title row + close; body follows at 16px. Title, subheading and body all
    // sit flush against the panel's own 24px padding — no extra inset (node
    // 490:17357: Content frame at x=24, with Title, Description and Body Text
    // all at x=0 inside it).
    //
    // ONLY the title shares its line with the close button. The subheading
    // drops BELOW that row at the full content width — Figma measures it at
    // 432 on a 480 panel (node 505:10497), i.e. NOT inset by the 36px X.
    // Nesting it in the title's flex-1 box cost it those 36px, which is what
    // wrapped the last word of a description that otherwise fits on one line.
    interior = (
      <View className={interiorClass}>
        <View className={cn('w-full gap-1', centered && 'items-center', headerClassName)}>
          <View className="w-full flex-row items-start">
            <View className={cn('min-w-0 flex-1', centered && 'items-center')}>
              <DialogTitle>{title}</DialogTitle>
            </View>
            {titleControls}
          </View>
          {subheading != null && <DialogSubheading>{subheading}</DialogSubheading>}
        </View>
        {body}
      </View>
    );
  }

  return (
    <DialogContext.Provider value={{ align, onClose: onClosePress, testID }}>
      <View
        testID={testID}
        // `hug` drops the size's min-height (twMerge collapses the conflict)
        // so case-by-case dialogs can hug their content instead of the DS
        // floor. Caller `className` merges last, so explicit min-h overrides
        // keep winning either way.
        className={cn(
          dialogPanelVariants({ size }),
          hug && 'min-h-0',
          panelClassName,
          className,
        )}
        nativeProps={panelStyle ? { style: panelStyle } : undefined}>
        {/* Frost. Web is a no-op (the CVA's `web:backdrop-blur-[27px]` does it);
            native draws an expo-blur BlurView behind the interior, because RN
            has no `backdrop-filter`. First child + `absolute inset-0`, so it
            sits under the content the panel already positions. */}
        <DialogGlassBackground
          radius={DIALOG_PANEL_RADIUS_PX[size ?? 'sm']}
          isDark={isDark}
        />
        {interior}
        {title != null && footer != null && (
          <DialogFooter layout={footerLayout}>{footer}</DialogFooter>
        )}
      </View>
    </DialogContext.Provider>
  );
}
